<template>
    <Teleport to="body">
        <Transition
            enter-active-class="transition-opacity duration-300"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="show"
                class="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 md:p-6 lg:p-8 bg-black/60 backdrop-blur-md"
                @click="closeModal"
            >
                <Transition
                    enter-active-class="transition-all duration-500 ease-out"
                    enter-from-class="opacity-0 scale-95 -translate-y-4"
                    enter-to-class="opacity-100 scale-100 translate-y-0"
                    leave-active-class="transition-all duration-300 ease-in"
                    leave-from-class="opacity-100 scale-100 translate-y-0"
                    leave-to-class="opacity-0 scale-95 translate-y-4"
                >
                    <div
                        v-if="show"
                        class="relative w-full max-w-[96vw] sm:max-w-xl md:max-w-2xl lg:max-w-4xl xl:max-w-5xl"
                        @click.stop
                    >
                        <!-- Main popup card -->
                        <div class="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-slate-700/50">
                            <!-- Close button -->
                            <button
                                class="absolute top-3 right-3 sm:top-4 sm:right-4 md:top-6 md:right-6 z-30 p-2 sm:p-2.5 rounded-full bg-slate-800/90 hover:bg-red-600 border border-slate-700 hover:border-red-500 text-slate-400 hover:text-white shadow-lg transition-all duration-300 group backdrop-blur-sm"
                                @click="closeModal"
                                aria-label="Close"
                            >
                                <svg class="w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-300 group-hover:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
                                </svg>
                            </button>

                            <!-- Content wrapper -->
                            <div class="relative flex flex-col lg:flex-row">
                                <!-- Image section with overlay content on mobile -->
                                <div class="relative w-full lg:w-1/2">
                                    <!-- Image container -->
                                    <div class="relative min-h-[500px] xs:min-h-[550px] sm:min-h-[600px] md:min-h-[650px] lg:min-h-[500px] xl:min-h-[600px] overflow-hidden">
                                        <!-- Image -->
                                        <img
                                            v-if="image"
                                            :src="image"
                                            :alt="title || 'Welcome'"
                                            class="absolute inset-0 w-full h-full object-cover object-center"
                                        />
                                        
                                        <!-- Fallback gradient -->
                                        <div v-else class="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700"></div>
                                        
                                        <!-- Soft gradient overlay -->
                                        <div class="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5 opacity-30"></div>
                                        
                                        <!-- Dark gradient overlay for text readability on mobile -->
                                        <div class="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/60 to-slate-900/30 lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-slate-900/10"></div>
                                    </div>

                                    <!-- Mobile overlay content (visible only on mobile) -->
                                    <div class="absolute inset-0 lg:hidden flex flex-col justify-end p-6 sm:p-8 md:p-10">
                                        <div class="space-y-4 sm:space-y-5 md:space-y-6">
                                            <!-- Title -->
                                            <h2 v-if="title" class="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight tracking-tight drop-shadow-2xl">
                                                {{ title }}
                                            </h2>

                                            <!-- Description -->
                                            <p v-if="description" class="text-base sm:text-lg md:text-xl text-slate-200 leading-relaxed drop-shadow-lg">
                                                {{ description }}
                                            </p>

                                            <!-- Divider -->
                                            <div v-if="title || description" class="w-16 sm:w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shadow-lg shadow-blue-500/50"></div>

                                            <!-- Action buttons -->
                                            <div class="flex flex-col xs:flex-row gap-3 sm:gap-4 pt-1 sm:pt-2">
                                                <button
                                                    class="group relative px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold text-sm sm:text-base rounded-xl shadow-xl shadow-blue-600/40 hover:shadow-2xl hover:shadow-blue-600/60 transition-all duration-300 overflow-hidden"
                                                    @click="closeModal"
                                                >
                                                    <span class="relative z-10 flex items-center justify-center gap-2">
                                                        <span>Get Started</span>
                                                        <svg class="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                                                        </svg>
                                                    </span>
                                                    <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                                                </button>
                                                
                                                <button
                                                    class="px-6 sm:px-8 py-3.5 sm:py-4 bg-slate-800/80 hover:bg-slate-800 border border-slate-600/70 hover:border-slate-500 text-slate-200 hover:text-white font-semibold text-sm sm:text-base rounded-xl transition-all duration-300 backdrop-blur-md shadow-lg"
                                                    @click="closeModal"
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Desktop text content section (visible only on desktop) -->
                                <div class="hidden lg:flex relative w-full lg:w-1/2 p-8 lg:p-10 xl:p-14 flex-col justify-center">
                                    <!-- Content -->
                                    <div class="space-y-5 lg:space-y-6">
                                        <!-- Title -->
                                        <h2 v-if="title" class="text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight">
                                            {{ title }}
                                        </h2>

                                        <!-- Description -->
                                        <p v-if="description" class="text-lg lg:text-xl text-slate-300 leading-relaxed">
                                            {{ description }}
                                        </p>

                                        <!-- Divider -->
                                        <div v-if="title || description" class="w-16 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>

                                        <!-- Action buttons -->
                                        <div class="flex flex-col xs:flex-row gap-4 pt-2">
                                            <button
                                                class="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold text-base rounded-xl shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/50 transition-all duration-300 overflow-hidden"
                                                @click="closeModal"
                                            >
                                                <span class="relative z-10 flex items-center justify-center gap-2">
                                                    <span>Get Started</span>
                                                    <svg class="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                                                    </svg>
                                                </span>
                                                <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                                            </button>
                                            
                                            <button
                                                class="px-8 py-4 bg-slate-700/50 hover:bg-slate-700 border border-slate-600/50 hover:border-slate-500 text-slate-300 hover:text-white font-semibold text-base rounded-xl transition-all duration-300 backdrop-blur-sm"
                                                @click="closeModal"
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'

const props = defineProps({
    enabled: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: ''
    },
    description: {
        type: String,
        default: ''
    },
    image: {
        type: String,
        default: null
    }
})

const show = ref(false)
const hasShownThisSession = ref(false)

const closeModal = () => {
    show.value = false
    hasShownThisSession.value = true
    sessionStorage.setItem('loginPopupShown', 'true')
}

onMounted(() => {
    const alreadyShownSession = sessionStorage.getItem('loginPopupShown')
    
    if (props.enabled && !alreadyShownSession && !hasShownThisSession.value) {
        setTimeout(() => {
            show.value = true
        }, 1000)
    }
})

watch(() => props.enabled, (newValue) => {
    if (!newValue) {
        show.value = false
    }
})
</script>

